'use client'

import React from 'react'
import { Star, ShoppingCart, Heart } from 'lucide-react'
import { getProductStock, inferInventoryMode } from '../utils/productInventory'
import { LaunchBadge, LaunchCountdown, useProductLaunchState } from './LaunchCountdown'

const getCardStock = (product) => {
  if (product.product_type === 'combo' || product.item_type === 'combo') {
    const directStock = product.calculated_stock ?? product.available_stock ?? product.stock
    if (directStock !== undefined && directStock !== null) {
      return Number(directStock) || 0
    }
    return 0
  }

  const directStock = product.calculated_stock ?? product.available_stock ?? product.stock
  if (directStock !== undefined && directStock !== null) {
    return Number(directStock) || 0
  }
  return getProductStock(product, inferInventoryMode(product))
}

export const getProductCardImage = (product) => {
  if (product.product_type === 'combo' || product.item_type === 'combo') {
    return product.thumbnail || product.images?.[0] || product.image || '/placeholder.svg'
  }

  if (product.colors?.length > 0 && product.color_images) {
    const firstColor = product.colors[0]
    if (product.color_images[firstColor]?.[0]) {
      return product.color_images[firstColor][0]
    }
    const normalizedSearch = firstColor.trim().toLowerCase()
    const matchingKey = Object.keys(product.color_images).find(
      (key) => key.trim().toLowerCase() === normalizedSearch
    )
    if (matchingKey && product.color_images[matchingKey]?.[0]) {
      return product.color_images[matchingKey][0]
    }
    const firstAvailableColorImages = Object.values(product.color_images).find((images) => images?.length > 0)
    if (firstAvailableColorImages?.[0]) {
      return firstAvailableColorImages[0]
    }
  }
  return product.images?.[0] || product.thumbnail || product.image || '/placeholder.svg'
}

export default function ProductCard({
  product,
  onOpen,
  onAddToCart,
  onWishlistToggle,
  isWishlisted = false,
  badge,
  badgeClass = 'bg-red-600 text-white',
  layout = 'carousel',
  showStock = true,
  showBadge = true,
  onLaunchBlocked,
}) {
  const stock = getCardStock(product)
  const launch = useProductLaunchState(product)
  const isGrid = layout === 'grid'
  const cardClass = isGrid
    ? 'group cursor-pointer overflow-hidden rounded-lg border border-white/10 bg-[#070707] flex flex-col h-full shadow-lg shadow-black/50 hover:shadow-xl hover:shadow-black/70 transition-all duration-300'
    : 'group cursor-pointer overflow-hidden rounded-lg border border-white/10 bg-[#070707] flex-shrink-0 snap-start w-[calc(50vw-24px)] min-w-[165px] max-w-[300px] md:w-[280px] flex flex-col h-full shadow-lg shadow-black/50 hover:shadow-xl hover:shadow-black/70 transition-all duration-300'
  const canPurchase = !launch.locked && (stock > 0 || product.product_type === 'combo' || product.item_type === 'combo')

  return (
    <div onClick={() => onOpen?.(product)} className={cardClass}>
      <div className="relative aspect-[4/3] overflow-hidden bg-white flex-shrink-0">
        <img
          src={getProductCardImage(product)}
          alt={product.name}
          className="w-full h-full object-cover"
        />

        <div className="absolute top-3 left-3 flex flex-col gap-2 z-10">
          <LaunchBadge product={product} />
          {showBadge && badge && !launch.badge && (
            <div className={`text-xs font-black uppercase px-3 py-1 rounded ${badgeClass}`}>
              {badge}
            </div>
          )}
          {showStock && (
            <div className={`text-xs font-black uppercase px-3 py-1 rounded ${stock > 0 ? 'bg-black/75 text-white' : 'bg-red-600 text-white'}`}>
              {stock > 0 ? 'In Stock' : 'Out of Stock'}
            </div>
          )}
        </div>

        <div className="absolute top-3 right-3 flex flex-col gap-2 z-10">
          <button
            onClick={(e) => {
              e.stopPropagation()
              onWishlistToggle?.(product)
            }}
            className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md ${isWishlisted ? 'bg-[#ffcc32] text-black' : 'bg-black/75 text-white'}`}
            title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart size={16} fill={isWishlisted ? 'currentColor' : 'none'} />
          </button>
        </div>

        <div className="absolute bottom-3 left-3 right-14 z-10">
          <LaunchCountdown product={product} compact />
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation()
            if (launch.locked) {
              onLaunchBlocked?.(product, launch)
              return
            }
            onAddToCart?.(product)
          }}
          disabled={!canPurchase && launch.locked}
          className={`absolute bottom-3 right-3 w-9 h-9 text-white rounded-full flex items-center justify-center shadow-md z-10 ${canPurchase ? 'bg-red-600' : 'bg-zinc-700 cursor-not-allowed'}`}
          title={launch.locked ? (launch.badge || 'Coming Soon') : 'Add to cart'}
        >
          <ShoppingCart size={16} />
        </button>
      </div>

      <div className="p-2.5 flex flex-col flex-grow">
        <h3
          className="text-white text-base md:text-lg font-bold uppercase line-clamp-2 leading-relaxed min-h-[2.5rem] max-h-[2.5rem]"
          style={{ fontFamily: 'Arial, sans-serif', fontWeight: 700, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.25rem', letterSpacing: '0.5px' }}
        >
          {product.name}
        </h3>

        <div className="flex items-center gap-1 mt-2 min-h-[16px]">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              size={10}
              className={
                i < Math.round(Number(product.rating || 0))
                  ? 'fill-[#ffcc32] text-[#ffcc32]'
                  : 'text-zinc-700'
              }
            />
          ))}
        </div>

        <div className="mt-2 mb-2">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-white font-black text-lg md:text-xl leading-relaxed tracking-wide" style={{ fontFamily: 'Arial, sans-serif' }}>
              Rs. {(product.price || 0).toLocaleString()}
            </span>
            {product.original_price && (
              <span className="text-zinc-400 line-through text-sm leading-relaxed" style={{ fontFamily: 'Arial, sans-serif' }}>
                Rs. {(product.original_price || 0).toLocaleString()}
              </span>
            )}
          </div>
          {product.original_price && (
            <div className="text-[#ffcc32] text-xs font-bold leading-relaxed" style={{ fontFamily: 'Arial, sans-serif' }}>
              Save {Math.round(((product.original_price - product.price) / product.original_price) * 100)}%
            </div>
          )}
        </div>

        <div className="flex-grow" />

        <button
          onClick={(e) => {
            e.stopPropagation()
            onOpen?.(product)
          }}
          className={`mt-2 w-full rounded-full py-2 text-sm font-bold uppercase ${launch.locked ? 'bg-zinc-700 text-zinc-300' : 'bg-red-600 text-white'}`}
          style={{ fontFamily: 'Arial, sans-serif', fontWeight: 800, letterSpacing: '1px' }}
        >
          {launch.locked ? (launch.badge || 'Coming Soon') : 'SHOP NOW'}
        </button>
      </div>
    </div>
  )
}